import moment, {Moment} from 'moment';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {TABLE_MAX_COLUMNS} from 'notebook/tables/constants/TableLayoutConstants';
import {RelativeCellAddress} from 'notebook/tables/excess/EvaluationConstants';
import {Environment} from 'notebook/tables/excess/Evaluator';
import {evalFunction} from 'notebook/tables/excess/FunctionEval';
import {getFunction} from 'notebook/tables/excess/functions';
import * as util from 'notebook/tables/excess/Util';
import {pow} from 'notebook/tables/excess/util/fdlibm';
import {makeUniformRange} from 'notebook/tables/excess/util/uniformRange';
import {
  coerceToSingleValue,
  isDatetimeTypedValue,
  isDateTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  ErrorTypedValue,
  NumberTypedValue,
  ScientificTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {Optional} from 'util/Types';

export function getPrecisionIfScientific(astValues: Array<Optional<TypedValue>>): Optional<number> {
  const precisions: Array<number> = [];
  if (astValues.length === 0) {
    return null;
  }
  const firstValue = astValues[0];
  if (firstValue == null || firstValue.type !== util.VALUE_TYPES.SCIENTIFIC) {
    return null;
  }
  for (const value of astValues) {
    if (value != null && value.type === util.VALUE_TYPES.SCIENTIFIC) {
      const defaultPrecision = value.defaultPrecision;
      if (defaultPrecision != null) {
        precisions.push(defaultPrecision);
      }
    }
  }
  if (precisions.length === 0) {
    return null;
  }
  return Math.max(...precisions);
}

/**
 * @returns whether `value` is a number or a date(time) that can be converted into a number.
 */
function isNumericLike(value: number | string | Moment | null): value is number | Moment {
  return typeof value === 'number' || moment.isMoment(value);
}

/**
 * @returns value if value is a number, or the number of days since epoch if value is a Moment.
 * @throws if value is not a number or a Moment.
 *
 * `useStableEpoch` selects the timezone-independent (UTC-anchored) serial when a snapshot timezone is in
 * effect, so the number is stable across evaluators; false keeps the legacy process-local serial. The
 * serial itself carries no timezone — see {@link util.momentToDaysSinceEpoch} — BNCH-161763.
 */
function convertToNumber(value: number | Moment, useStableEpoch: boolean): number {
  assert(isNumericLike(value), 'Expected value to be a number or moment');
  return moment.isMoment(value) ? util.momentToDaysSinceEpoch(value, useStableEpoch) : value;
}

export abstract class ASTNode {
  eval(env: Environment): TypedValue {
    // Given an environment, returns the computed value of a cell
    //   For now, this is probably a number, could maybe be a string.
    // Should throw an error when sub-expressions are NaN / Infinity ?
    // Implemented by subclasses.
    throw new Error('eval not implemented');
  }

  // This is a mechanism for a node to modify its children.
  // It is currently used for LOOKUPFIELD function to be able to add dependency on nodes in addition to the
  // ones that are specified as function arguments. We don't expect this method to be implemented for
  // most of the node types.
  prepareChildren(rowIndex: number, columnDefinitions: Array<ColumnDefinition | null> | null): void {
    for (const child of this.childNodes()) {
      child.prepareChildren(rowIndex, columnDefinitions);
    }
  }

  childNodes(): Array<ASTNode> {
    return [];
  }
  /**
   * Enumerates the cells that this node needs in order to complete evaluation.
   * When env is present, node may optimize enumeration accordingly.
   * @param env The environment the node exists within (should contain fullTableMap and currentTable)
   */
  dependencies(env?: Environment): Set<RelativeCellAddress> {
    // A list of cell ids that this node's value depends on.
    // Implemented by subclasses.
    throw new Error('dependencies not implemented');
  }
  isSingular(): boolean {
    return true;
  }
  isTerminal(): boolean {
    // Returns if this subtree is a valid leaf in a parse tree.
    throw new Error('isTerminal is not implemented');
  }

  abstract equals(other: Optional<ASTNode>): boolean;
}

export class ReferenceErrorNode extends ASTNode {
  text: string = '#REF!';

  override eval(env: Environment): TypedValue {
    throw new util.ReferenceError('Invalid reference');
  }
  override dependencies(env?: Environment): Set<RelativeCellAddress> {
    return new Set();
  }
  override isTerminal(): boolean {
    return true;
  }
  equals(other: Optional<ASTNode>): boolean {
    return other instanceof ReferenceErrorNode;
  }
}

export enum UnaryOperatorType {
  PLUS = '+',
  NEGATION = '-',
}

export class UnaryOperator extends ASTNode {
  type: UnaryOperatorType;
  child: ASTNode;

  constructor({type, child}: {type: UnaryOperatorType; child: ASTNode}) {
    super();
    this.type = type;
    this.child = child;
  }

  override childNodes(): Array<ASTNode> {
    return [this.child];
  }
  override dependencies(env?: Environment): Set<RelativeCellAddress> {
    return this.child.dependencies(env);
  }
  equals(other: Optional<ASTNode>): boolean {
    if (!(other instanceof UnaryOperator)) {
      return false;
    }
    if (this.type !== other.type) {
      return false;
    }
    return this.child.equals(other.child);
  }

  override eval(env: Environment): TypedValue {
    const context = {languageTag: env.getLanguageTag(), timezone: env.getTimezone()};
    const timezone = context.timezone;
    const useStableEpoch = util.usesStableDateEpoch(timezone);
    const singleValue = coerceToSingleValue(this.child.eval(env), context);
    const childRawVal = singleValue.rawValue;
    const type = singleValue.type;
    const defaultPrecision = 'defaultPrecision' in singleValue ? singleValue.defaultPrecision : null;
    if (!isNumericLike(childRawVal)) {
      throw new util.ValueError(`Operator ${this.type} does not support '${childRawVal}'.`);
    }
    switch (this.type) {
      case UnaryOperatorType.PLUS:
        if (moment.isMoment(childRawVal)) {
          const value = util.daysSinceEpochToDatetimeString(
            convertToNumber(childRawVal, useStableEpoch),
            timezone
          );
          return util.astValue({
            value,
            type: BaseTypedValueType.DATETIME,
          });
        }
        // TODO(TypeScript): remove "as any" in favor of ensuring type in [NUMBER, SCIENTIFIC, DATETIME]
        assertAndContinue(type === 'NUMBER' || type === 'SCIENTIFIC', 'Expected number or scientific type', {
          type,
        });
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any
        return util.astValue({rawValue: childRawVal, type, defaultPrecision} as any);
      case UnaryOperatorType.NEGATION:
        if (moment.isMoment(childRawVal)) {
          const value = util.daysSinceEpochToDatetimeString(
            -convertToNumber(childRawVal, useStableEpoch),
            timezone
          );
          return util.astValue({
            value,
            type: BaseTypedValueType.DATETIME,
          });
        }
        assertAndContinue(type === 'NUMBER' || type === 'SCIENTIFIC', 'Expected number or scientific type', {
          type,
        });
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any
        return util.astValue({rawValue: -childRawVal, type, defaultPrecision} as any);
      default:
        throw new Error(`Unexpected type found for unary operator: ${this.type}`);
    }
  }

  override isTerminal(): boolean {
    return this.child != null;
  }
}

export enum BinaryOperatorType {
  ADDITION = '+',
  SUBTRACTION = '-',
  MULTIPLICATION = '*',
  DIVISION = '/',
  EXPONENTIAL = '^',
  EQUALS = '=',
  NOT_EQUALS = '<>',
  LESS_THAN = '<',
  GREATER_THAN = '>',
  LESS_THAN_OR_EQUAL = '<=',
  GREATER_THAN_OR_EQUAL = '>=',
}

export class BinaryOperator extends ASTNode {
  type: BinaryOperatorType;
  left?: ASTNode;
  right?: ASTNode;

  // TODO(TypeScript): left and right should not be optional: BinaryOperator instantiation should not
  // happen in tokenizer.js (before left and right are known) but instead in parser.js.
  constructor({type, left, right}: {type: BinaryOperatorType; left?: ASTNode; right?: ASTNode}) {
    super();
    this.type = type;
    this.left = left;
    this.right = right;
  }

  _getLeftAndRight(): {left: ASTNode; right: ASTNode} {
    assert(Boolean(this.left && this.right), 'Expected left and right to be defined on BinaryOperator');
    return {
      left: this.left!,
      right: this.right!,
    };
  }

  override childNodes(): Array<ASTNode> {
    const {left, right} = this._getLeftAndRight();
    return [left, right];
  }

  override dependencies(env?: Environment): Set<RelativeCellAddress> {
    const {left, right} = this._getLeftAndRight();
    return new Set([...left.dependencies(env), ...right.dependencies(env)]);
  }

  equals(other: Optional<ASTNode>): boolean {
    if (!(other instanceof BinaryOperator)) {
      return false;
    }
    if (this.type !== other.type) {
      return false;
    }
    const leftEqual = this.left != null ? this.left.equals(other.left) : other.left == null;
    const rightEqual = this.right != null ? this.right.equals(other.right) : other.right == null;
    return leftEqual && rightEqual;
  }

  override eval(env: Environment): TypedValue {
    const {left, right} = this._getLeftAndRight();
    const context = {languageTag: env.getLanguageTag(), timezone: env.getTimezone()};
    const timezone = context.timezone;
    const useStableEpoch = util.usesStableDateEpoch(timezone);
    // Keep the pre-coercion values: coerceToSingleValue flattens DATE and DATETIME into the same shape, and
    // subtraction needs to tell them apart to choose calendar days over elapsed time.
    const leftTypedValue = left.eval(env);
    const rightTypedValue = right.eval(env);
    const leftASTValue = coerceToSingleValue(leftTypedValue, context);
    const rightASTValue = coerceToSingleValue(rightTypedValue, context);
    let {rawValue: leftValue} = leftASTValue;
    let {rawValue: rightValue} = rightASTValue;

    if (
      [
        BinaryOperatorType.LESS_THAN_OR_EQUAL,
        BinaryOperatorType.LESS_THAN,
        BinaryOperatorType.EQUALS,
        BinaryOperatorType.NOT_EQUALS,
        BinaryOperatorType.GREATER_THAN_OR_EQUAL,
        BinaryOperatorType.GREATER_THAN,
      ].includes(this.type)
    ) {
      const booleanValue = this._booleanEval(leftValue, rightValue, useStableEpoch);
      return util.astValue({
        type: BaseTypedValueType.BOOLEAN,
        booleanValue,
      });
    }

    if (!isNumericLike(leftValue) && leftValue != null) {
      throw new util.ValueError(`Operator ${this.type} does not support '${leftValue}'.`);
    }
    if (!isNumericLike(rightValue) && rightValue != null) {
      throw new util.ValueError(`Operator ${this.type} does not support '${rightValue}'.`);
    }

    leftValue ??= 0;
    rightValue ??= 0;

    if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
      // If both operands are dates, return a number in days
      // If either operand carries a time of day, measure physical elapsed time; two bare calendar dates
      // subtract to whole days, so a DST transition day still counts as 1. Only with a snapshot timezone —
      // without one both operands are parsed the same way, and changing the units would move existing
      // results.
      const measureElapsedTime =
        useStableEpoch && (isDatetimeTypedValue(leftTypedValue) || isDatetimeTypedValue(rightTypedValue));
      // We handle subtraction specially because it is likely to introduce floating point errors
      const rawValue =
        this.type === BinaryOperatorType.SUBTRACTION
          ? util.momentDiffInDays(leftValue, rightValue, measureElapsedTime)
          : this._numericEval(
              util.momentToDaysSinceEpoch(leftValue, useStableEpoch),
              util.momentToDaysSinceEpoch(rightValue, useStableEpoch)
            );
      return util.astValue({rawValue, type: BaseTypedValueType.NUMBER});
    } else if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      // If both operands are numbers, do normal arithmetic
      const rawValue = this._numericEval(leftValue, rightValue);
      return util.astValue({
        rawValue,
        type: leftASTValue.type,
        defaultPrecision: getPrecisionIfScientific([
          leftASTValue as ScientificTypedValue | NumberTypedValue,
          rightASTValue as ScientificTypedValue | NumberTypedValue,
        ]),
      } as NumberTypedValue | ScientificTypedValue);
    } else {
      // If one operand is a date and the other is a number, return a date.
      const calendarDayShift = this._calendarDayShift({
        timezone,
        leftValue,
        rightValue,
        leftTypedValue,
        rightTypedValue,
      });
      if (calendarDayShift != null) {
        return util.astValue({
          value: util.momentToDatetimeString(
            util.momentPlusDays(calendarDayShift.date, calendarDayShift.days)
          ),
          type: BaseTypedValueType.DATETIME,
        });
      }
      const leftNumber = convertToNumber(leftValue, useStableEpoch);
      const rightNumber = convertToNumber(rightValue, useStableEpoch);
      const value = util.daysSinceEpochToDatetimeString(this._numericEval(leftNumber, rightNumber), timezone);
      return util.astValue({
        value,
        type: BaseTypedValueType.DATETIME,
      });
    }
  }

  /**
   * The date to shift and the whole number of calendar days to shift it by, or null when the operation is
   * not a day shift and should go through the epoch instead.
   *
   * Shifting a bare date by a whole number of days is calendar arithmetic: `+ 1` means the next day at the
   * same wall clock, so a span containing a DST transition must not leave the result an hour either side of
   * midnight. Everything else keeps the serial round trip — a datetime operand, because `+ 1` there is 24
   * physical hours; a fractional shift, because it names a time of day and should reach it in physical
   * time; `n - date`, which is not a shift of anything; and multiplication and division, which have no
   * calendar meaning. Without a snapshot timezone the operand has no zone worth shifting in, and the legacy
   * process-local epoch already measures calendar days.
   */
  _calendarDayShift({
    timezone,
    leftValue,
    rightValue,
    leftTypedValue,
    rightTypedValue,
  }: {
    timezone: Optional<string>;
    leftValue: number | Moment;
    rightValue: number | Moment;
    leftTypedValue: TypedValue;
    rightTypedValue: TypedValue;
  }): {date: Moment; days: number} | null {
    const isAddition = this.type === BinaryOperatorType.ADDITION;
    if (!isAddition && this.type !== BinaryOperatorType.SUBTRACTION) {
      return null;
    }
    if (timezone == null || !util.usesStableDateEpoch(timezone)) {
      return null;
    }
    // Exactly one operand is a moment here; the other is how many days to shift it by.
    if (moment.isMoment(leftValue)) {
      if (!isDateTypedValue(leftTypedValue) || typeof rightValue !== 'number') {
        return null;
      }
      return Number.isInteger(rightValue)
        ? {date: leftValue, days: isAddition ? rightValue : -rightValue}
        : null;
    }
    // With the date on the right, only addition is a shift of it: `n - date` counts days back from the
    // epoch, which is not a date to shift.
    if (!isAddition || !moment.isMoment(rightValue) || !isDateTypedValue(rightTypedValue)) {
      return null;
    }
    return Number.isInteger(leftValue) ? {date: rightValue, days: leftValue} : null;
  }

  _numericEval(leftValue: number, rightValue: number): number {
    switch (this.type) {
      case BinaryOperatorType.ADDITION:
        return leftValue + rightValue;
      case BinaryOperatorType.SUBTRACTION:
        return leftValue - rightValue;
      case BinaryOperatorType.MULTIPLICATION:
        return leftValue * rightValue;
      case BinaryOperatorType.DIVISION:
        return this._divide(leftValue, rightValue);
      case BinaryOperatorType.EXPONENTIAL:
        return pow(leftValue, rightValue);
      default:
        throw new Error(`Unexpected operator: ${this.type}`);
    }
  }

  _booleanEval(
    leftValue: number | string | Moment | null,
    rightValue: number | string | Moment | null,
    useStableEpoch: boolean = false
  ): boolean {
    switch (this.type) {
      case BinaryOperatorType.EQUALS:
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          // handle floating point equality correctly
          return util.capMaxFloatDecimalDigits(leftValue) === util.capMaxFloatDecimalDigits(rightValue);
        } else if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
          return leftValue.isSame(rightValue);
        } else if (isNumericLike(leftValue) && isNumericLike(rightValue)) {
          return convertToNumber(leftValue, useStableEpoch) === convertToNumber(rightValue, useStableEpoch);
        } else {
          return (leftValue ?? '') === (rightValue ?? '');
        }
      case BinaryOperatorType.NOT_EQUALS:
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          // handle floating point equality correctly
          return util.capMaxFloatDecimalDigits(leftValue) !== util.capMaxFloatDecimalDigits(rightValue);
        } else if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
          return !leftValue.isSame(rightValue);
        } else if (isNumericLike(leftValue) && isNumericLike(rightValue)) {
          return convertToNumber(leftValue, useStableEpoch) !== convertToNumber(rightValue, useStableEpoch);
        } else {
          return (leftValue ?? '') !== (rightValue ?? '');
        }
      case BinaryOperatorType.GREATER_THAN:
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          return leftValue > rightValue;
        } else if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
          return leftValue.isAfter(rightValue);
        } else if (isNumericLike(leftValue) && isNumericLike(rightValue)) {
          return convertToNumber(leftValue, useStableEpoch) > convertToNumber(rightValue, useStableEpoch);
        } else if (typeof leftValue === 'number' && typeof rightValue === 'string') {
          return false;
        } else if (typeof leftValue === 'string' && typeof rightValue === 'number') {
          return true;
        }
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (leftValue === null && rightValue === null) {
          return false;
        }
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (rightValue === null) {
          return true;
        } else {
          return false;
        }
      case BinaryOperatorType.GREATER_THAN_OR_EQUAL:
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          // handle floating point equality correctly
          return util.capMaxFloatDecimalDigits(leftValue) >= util.capMaxFloatDecimalDigits(rightValue);
        } else if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
          return leftValue.isSameOrAfter(rightValue) || leftValue === rightValue;
        } else if (isNumericLike(leftValue) && isNumericLike(rightValue)) {
          return convertToNumber(leftValue, useStableEpoch) >= convertToNumber(rightValue, useStableEpoch);
        } else if (typeof leftValue === 'number' && typeof rightValue === 'string') {
          return false;
        } else if (typeof leftValue === 'string' && typeof rightValue === 'number') {
          return true;
        }
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (leftValue === null && rightValue === null) {
          return true;
        }
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (rightValue === null) {
          return true;
        } else {
          return false;
        }
      case BinaryOperatorType.LESS_THAN:
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          return leftValue < rightValue;
        } else if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
          return leftValue.isBefore(rightValue);
        } else if (isNumericLike(leftValue) && isNumericLike(rightValue)) {
          return convertToNumber(leftValue, useStableEpoch) < convertToNumber(rightValue, useStableEpoch);
        } else if (typeof leftValue === 'number' && typeof rightValue === 'string') {
          return true;
        } else if (typeof leftValue === 'string' && typeof rightValue === 'number') {
          return false;
        }
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (leftValue === null && rightValue === null) {
          return false;
        } else {
          return false;
        }
      case BinaryOperatorType.LESS_THAN_OR_EQUAL:
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          // handle floating point equality correctly
          return util.capMaxFloatDecimalDigits(leftValue) <= util.capMaxFloatDecimalDigits(rightValue);
        } else if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
          return leftValue.isSameOrBefore(rightValue) || leftValue === rightValue;
        } else if (isNumericLike(leftValue) && isNumericLike(rightValue)) {
          return convertToNumber(leftValue, useStableEpoch) <= convertToNumber(rightValue, useStableEpoch);
        } else if (typeof leftValue === 'number' && typeof rightValue === 'string') {
          return true;
        } else if (typeof leftValue === 'string' && typeof rightValue === 'number') {
          return false;
        }
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (leftValue === null && rightValue === null) {
          return true;
        } // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (leftValue === null) {
          return true;
        } else {
          return false;
        }
      default:
        throw new Error(`Unexpected operator: ${this.type}`);
    }
  }

  _divide(left: number, right: number): number {
    if (right === 0) {
      throw new util.DivZeroError('Divisor cannot be 0');
    } else {
      return left / right;
    }
  }

  override isTerminal(): boolean {
    return this.left != null && this.right != null;
  }
}

export class FunctionOperator extends ASTNode {
  type: string;
  children: Array<ASTNode>;

  constructor({type, children}: {type: string; children: Array<ASTNode>}) {
    super();
    this.type = type;
    this.children = children;
  }

  // This is a mechanism for a function to add dependency on nodes in addition to the
  // ones that are specified as function arguments. A function may add extra function arguments based on
  // column definitions. An example is that LOOKUPFIELD function may add dependency on the STAGE_RUN cell.
  override prepareChildren(rowIndex: number, columnDefinitions: Array<ColumnDefinition | null> | null): void {
    super.prepareChildren(rowIndex, columnDefinitions);
    const fn = getFunction(this.type);
    if (fn != null && fn.getExtraChildren != null) {
      this.children = [...this.children, ...fn.getExtraChildren({rowIndex, columnDefinitions, node: this})];
    }
  }

  override childNodes(): Array<ASTNode> {
    return this.children;
  }
  override dependencies(env?: Environment): Set<RelativeCellAddress> {
    const deps = new Set<RelativeCellAddress>();
    for (const child of this.children) {
      for (const dep of child.dependencies(env)) {
        deps.add(dep);
      }
    }
    return deps;
  }
  equals(other: Optional<ASTNode>): boolean {
    if (!(other instanceof FunctionOperator)) {
      return false;
    }
    if (this.type !== other.type) {
      return false;
    }
    if (this.children.length !== other.children.length) {
      return false;
    }
    for (let i = 0; i < this.children.length; i++) {
      if (!this.children[i].equals(other.children[i])) {
        return false;
      }
    }
    return true;
  }

  override eval(env: Environment): TypedValue {
    return evalFunction(this, env);
  }

  override isTerminal(): boolean {
    return this.children != null;
  }
}

export interface CellLock {
  row: boolean;
  col: boolean;
}

export interface RangeLock {
  startRow: boolean;
  startCol: boolean;
  endRow: boolean;
  endCol: boolean;
}

export abstract class AbsoluteCellReference extends ASTNode {
  abstract text: string;

  getTable(): string | undefined | null {
    throw new Error('Not implemented');
  }

  getDoc(): string | undefined | null {
    throw new Error('Not implemented');
  }

  static _relativeIdFor(
    cellId: RelativeCellAddress,
    {
      anchorId,
      locks,
      includeTableAndDoc,
    }: {anchorId: RelativeCellAddress; locks: CellLock; includeTableAndDoc: boolean}
  ): {cellId: RelativeCellAddress; text: string} {
    let columnText;
    let relCol;
    let relRow;
    let rowText;
    const {row, column, table, document} = cellId;
    if (locks.row) {
      relRow = row;
      rowText = `R${relRow}`;
    } else {
      relRow = row - anchorId.row;
      rowText = `R[${relRow}]`;
    }

    if (locks.col) {
      relCol = column;
      columnText = `C${relCol}`;
    } else {
      relCol = column - anchorId.column;
      columnText = `C[${relCol}]`;
    }

    const docTablePrefix =
      table != null && includeTableAndDoc ? util.getDocTableReferencePrefix(document, table) : '';

    return {
      cellId: {row: relRow, column: relCol, table, document},
      text: `${docTablePrefix}${rowText}${columnText}`,
    };
  }

  toRelativeNode(anchorId: RelativeCellAddress): RelativeCellReference {
    throw new Error('toRelativeNode not implemented');
  }
}

export class Cell extends AbsoluteCellReference {
  _dependencies: Set<RelativeCellAddress>;
  id: RelativeCellAddress;
  text: string;
  locks: CellLock;

  override getTable(): string | undefined | null {
    return this.id.table;
  }

  override getDoc(): string | undefined | null {
    return this.id.document;
  }

  override eval(env: Environment): TypedValue {
    if (!this._validRef()) {
      throw new util.NameError(`Unknown range name: '${this.text}'`);
    }
    return env.get(this.id);
  }

  override dependencies(env?: Environment): Set<RelativeCellAddress> {
    return this._dependencies;
  }

  override toRelativeNode(anchorId: RelativeCellAddress): RelCell {
    const {cellId, text} = Cell._relativeIdFor(this.id, {
      anchorId,
      locks: this.locks,
      includeTableAndDoc: true,
    });
    return new RelCell({
      text,
      id: cellId,
      locks: _.clone(this.locks),
    });
  }

  constructor({id, text, locks}: {id: RelativeCellAddress; text: string; locks: CellLock}) {
    super();
    this.id = id;
    this.text = text;
    this.locks = _.defaults(locks || {}, {
      row: false,
      col: false,
    });
    if (this._validRef()) {
      this._dependencies = new Set([this.id]);
    } else {
      this._dependencies = new Set();
    }
  }

  equals(other: Optional<ASTNode>): boolean {
    return (
      other instanceof Cell &&
      isEqual(this.id, other.id) &&
      isEqual(this.locks, other.locks) &&
      this.text === other.text
    );
  }

  override isTerminal(): boolean {
    return true;
  }

  _validRef(): boolean {
    return this.id.column < TABLE_MAX_COLUMNS;
  }
}

export class CellRange extends AbsoluteCellReference {
  _dependencies?: Set<RelativeCellAddress>;
  start: RelativeCellAddress;
  end: RelativeCellAddress;
  locks: RangeLock;
  text: string;
  table?: string | null;
  document?: string | null;

  override getTable(): string | undefined | null {
    return this.start.table;
  }

  override getDoc(): string | undefined | null {
    return this.start.document;
  }

  override eval(env: Environment): TypedValue {
    if (!this._validRefs()) {
      throw new util.NameError(`Unknown range name: '${this.text}'`);
    }

    if (this._isMissingTableName(env, {resolveDocumentStrictly: true})) {
      const missingTableName = this.table ?? env.currentTable;
      // Every cell of a range over a nonexistent table evaluates to the same error, so share one value
      // across the range instead of evaluating each cell.
      return makeUniformRange(
        Math.abs(this.end.row - this.start.row) + 1,
        Math.abs(this.end.column - this.start.column) + 1,
        {
          type: BaseTypedValueType.ERROR,
          error: new util.ReferenceError(`Unresolved sheet name '${missingTableName}'.`),
        } satisfies ErrorTypedValue
      );
    }

    const {
      startRow: trimmedStartRow,
      startColumn: trimmedStartColumn,
      endRow: trimmedEndRow,
      endColumn: trimmedEndCol,
    } = this._calcTrimmedRange(env);

    const startRow = Math.min(trimmedStartRow, trimmedEndRow);
    const endRow = Math.max(trimmedStartRow, trimmedEndRow);
    const startCol = Math.min(trimmedStartColumn, trimmedEndCol);
    const endCol = Math.max(trimmedStartColumn, trimmedEndCol);

    // Returns a 2D array
    return util.astValue({
      type: BaseTypedValueType.ARRAY,
      rawValue: _.range(startRow, endRow + 1).map((row: number) =>
        util.astValue({
          type: BaseTypedValueType.ARRAY,
          rawValue: _.range(startCol, endCol + 1).map((column: number) => {
            // For cell ranges, we allow getting errors from cells to carry them around as ErrorTypedValues.
            // For most cases, we will throw the error as soon as we encounter it, but certain functions
            // like XLOOKUP allow for errors to be in the ranges of the arguments.
            const {value, error} = env.getValueWithError({
              row,
              column,
              table: this.table,
              document: this.document,
            });
            if (value) {
              return value;
            } else {
              return {
                type: BaseTypedValueType.ERROR,
                error,
              } satisfies ErrorTypedValue;
            }
          }),
        })
      ),
    });
  }

  /**
   * Calculates the trimmed range based on the number of rows and max number of columns in
   * the referenced table. This is to prevent extra work from being performed if the user enters range
   * like A0:Z99 on a Table with a single cell.
   *
   * If not enough information to determine the optimal dimensions is given in the env,
   * returns the current range.
   *
   * Note: also trims the start row and column since ranges can be in ascending or descending order.
   *
   * @param env the environment containing FullTableMap and the name of the currentTable being processed
   * @returns dict containing the trimmed startRow, startColumn, endRow, and endColumn
   */
  _calcTrimmedRange(env?: Environment): {
    startRow: number;
    startColumn: number;
    endRow: number;
    endColumn: number;
  } {
    if (env) {
      const tableName = this.table ?? env.currentTable;
      const documentId = this.document ? env.getNullableDocumentId(this.document) : env.currentDocumentId;

      if (tableName && documentId && env.fullTableMap[documentId]) {
        const table = env.fullTableMap[documentId][tableName];

        if (table != null && table.table != null) {
          const {start: startRow, end: endRow} = util.trimDimensionRange(
            this.start.row,
            this.end.row,
            table.table.length - 1
          );

          const maxWidth = Math.max(...table.table.map((row: Array<unknown>) => row.length));
          const {start: startColumn, end: endColumn} = util.trimDimensionRange(
            this.start.column,
            this.end.column,
            maxWidth - 1
          );

          return {startRow, startColumn, endRow, endColumn};
        }
      }
    }
    return {
      startRow: this.start.row,
      startColumn: this.start.column,
      endRow: this.end.row,
      endColumn: this.end.column,
    };
  }

  /**
   * Returns whether the table this range refers to does not exist in a resolvable document.
   *
   * With resolveDocumentStrictly, a document reference that cannot be resolved returns false so that
   * evaluation surfaces the same document resolution error as a cell-by-cell lookup would.
   */
  _isMissingTableName(
    env: Optional<Environment>,
    {resolveDocumentStrictly}: {resolveDocumentStrictly: boolean}
  ): boolean {
    if (env == null) {
      return false;
    }
    const tableName = this.table ?? env.currentTable;
    let documentId: string | null;
    if (this.document == null) {
      documentId = env.currentDocumentId;
    } else if (resolveDocumentStrictly) {
      try {
        documentId = env.getDocumentId(this.document);
      } catch (e) {
        if (e instanceof util.CellError) {
          return false;
        }
        throw e;
      }
    } else {
      documentId = env.getNullableDocumentId(this.document);
    }
    if (tableName == null || documentId == null) {
      return false;
    }
    const tables = env.fullTableMap[documentId];
    return tables != null && !(tableName in tables);
  }

  override dependencies(env?: Environment): Set<RelativeCellAddress> {
    if (this._dependencies != null) {
      return this._dependencies;
    }
    this._dependencies = new Set();

    if (this._isMissingTableName(env, {resolveDocumentStrictly: false})) {
      return this._dependencies;
    }

    const {startRow, startColumn, endRow, endColumn} = this._calcTrimmedRange(env);

    if (this._validRefs()) {
      for (
        let row = startRow, end = endRow, asc = startRow <= end;
        asc ? row <= end : row >= end;
        asc ? row++ : row--
      ) {
        for (
          let column = startColumn, end1 = endColumn, asc1 = startColumn <= end1;
          asc1 ? column <= end1 : column >= end1;
          asc1 ? column++ : column--
        ) {
          this._dependencies.add({row, column, table: this.table, document: this.document});
        }
      }
    }
    return this._dependencies;
  }

  override isTerminal(): boolean {
    return false;
  }

  override toRelativeNode(anchorId: RelativeCellAddress): RelCellRange {
    const {cellId: start, text: startText} = CellRange._relativeIdFor(this.start, {
      anchorId,
      locks: {row: this.locks.startRow, col: this.locks.startCol},
      includeTableAndDoc: true,
    });
    const {cellId: end, text: endText} = CellRange._relativeIdFor(this.end, {
      anchorId,
      locks: {row: this.locks.endRow, col: this.locks.endCol},
      includeTableAndDoc: false, // start cell includes the table and doc.
    });
    return new RelCellRange({
      text: `${startText}:${endText}`,
      start,
      end,
      locks: _.clone(this.locks),
    });
  }

  constructor({
    start,
    end,
    text,
    locks,
  }: {
    start: RelativeCellAddress;
    end: RelativeCellAddress;
    text: string;
    locks?: Partial<RangeLock>;
  }) {
    super();
    this.start = start;
    this.end = end;
    this.text = text;
    this.locks = _.defaults(locks || {}, {
      startRow: false,
      startCol: false,
      endRow: false,
      endCol: false,
    });
    if (this.start.table !== this.end.table) {
      throw new util.FormulaParseError('Formula parse error');
    }
    this.table = this.start.table;
    this.document = this.start.document;
  }

  override isSingular(): boolean {
    return false;
  }

  equals(other: Optional<ASTNode>): boolean {
    return (
      other instanceof CellRange &&
      isEqual(this.start, other.start) &&
      isEqual(this.end, other.end) &&
      isEqual(this.locks, other.locks) &&
      this.text === other.text
    );
  }

  _validRefs(): boolean {
    return this.start.column < TABLE_MAX_COLUMNS && this.end.column < TABLE_MAX_COLUMNS;
  }
}

export abstract class RelativeCellReference extends ASTNode {
  static _absoluteIdFor(
    cellId: RelativeCellAddress,
    {
      anchorId,
      locks,
      includeTableAndDoc,
    }: {anchorId: RelativeCellAddress; locks: CellLock; includeTableAndDoc: boolean}
  ): {cellId: RelativeCellAddress; text: string} {
    let absCol;
    let absRow;
    const {row, column, table, document} = cellId;
    if (locks.row) {
      absRow = row;
    } else {
      absRow = anchorId.row + row;
    }

    if (locks.col) {
      absCol = column;
    } else {
      absCol = anchorId.column + column;
    }

    return {
      cellId: {row: absRow, column: absCol, table, document},
      text: util.toCellReferenceTokenText(
        {
          row: absRow,
          column: absCol,
          table: includeTableAndDoc ? table : undefined,
          document: includeTableAndDoc ? document : undefined,
        },
        locks
      ),
    };
  }

  toAbsoluteNode(anchorId: RelativeCellAddress): AbsoluteCellReference | ReferenceErrorNode {
    throw new Error('toAbsoluteNode not implemented');
  }

  abstract text: string;
}

export class RelCell extends RelativeCellReference {
  id: RelativeCellAddress;
  text: string;
  locks: CellLock;

  constructor({id, text, locks}: {id: RelativeCellAddress; text: string; locks: Partial<CellLock>}) {
    super();
    this.id = id;
    this.text = text;
    this.locks = _.defaults(locks || {}, {
      row: false,
      col: false,
    });
  }

  equals(other: Optional<ASTNode>): boolean {
    return (
      other instanceof RelCell &&
      isEqual(this.id, other.id) &&
      isEqual(this.locks, other.locks) &&
      this.text === other.text
    );
  }

  override toAbsoluteNode(anchorId: RelativeCellAddress): Cell | ReferenceErrorNode {
    const {cellId, text} = RelCell._absoluteIdFor(this.id, {
      anchorId,
      locks: this.locks,
      includeTableAndDoc: true,
    });
    if (cellId.row < 0 || cellId.column < 0) {
      return new ReferenceErrorNode();
    }
    return new Cell({
      text,
      id: cellId,
      locks: _.clone(this.locks),
    });
  }
}

export class RelCellRange extends RelativeCellReference {
  locks: RangeLock;
  start: RelativeCellAddress;
  end: RelativeCellAddress;
  text: string;

  constructor({
    start,
    end,
    text,
    locks,
  }: {
    start: RelativeCellAddress;
    end: RelativeCellAddress;
    text: string;
    locks?: Partial<RangeLock>;
  }) {
    super();
    this.start = start;
    this.end = end;
    this.text = text;
    this.locks = _.defaults(locks || {}, {
      startRow: false,
      startCol: false,
      endRow: false,
      endCol: false,
    });
  }

  override toAbsoluteNode(anchorId: RelativeCellAddress): CellRange | ReferenceErrorNode {
    const {cellId: start, text: startText} = RelCell._absoluteIdFor(this.start, {
      anchorId,
      locks: {row: this.locks.startRow, col: this.locks.startCol},
      includeTableAndDoc: true,
    });
    const {cellId: end, text: endText} = RelCell._absoluteIdFor(this.end, {
      anchorId,
      locks: {row: this.locks.endRow, col: this.locks.endCol},
      includeTableAndDoc: false,
    });
    if (start.row < 0 || start.column < 0 || end.row < 0 || end.column < 0) {
      return new ReferenceErrorNode();
    }

    return new CellRange({
      text: `${startText}:${endText}`,
      start,
      end,
      locks: _.clone(this.locks),
    });
  }

  equals(other: Optional<ASTNode>): boolean {
    return (
      other instanceof RelCellRange &&
      isEqual(this.start, other.start) &&
      isEqual(this.end, other.end) &&
      isEqual(this.locks, other.locks) &&
      this.text === other.text
    );
  }
}

export class Value extends ASTNode {
  override eval(env: Environment | null = null): TypedValue {
    return util.astValue(this.typedValue);
  }
  override dependencies(env?: Environment): Set<RelativeCellAddress> {
    return new Set();
  }
  constructor(readonly typedValue: TypedValue) {
    super();
  }

  override isTerminal(): boolean {
    return true;
  }

  /**
   * This indicates if two values are identical. Only used for tests via `expect(this).equals(other)`,
   * and may behave unexpectedly if two similar values are independently constructed but have denormalized
   * data that may vary slightly.
   */
  equals(other: Optional<ASTNode>): boolean {
    return other instanceof Value && isEqual(this.typedValue, other.typedValue);
  }
}
